import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';

export type MaintenanceMfaChannel = 'EMAIL' | 'AUTHENTICATOR_APP' | 'SMS';

export interface MfaDeliveryCapabilities {
  smsAvailable: boolean;
  smsDestination: string | null;
}

export type MaintenanceSession = {
  authenticated: boolean;
  userId?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  accountType?: string | null;
  roles?: string[] | null;
};

export type MaintenancePreAuthResponse = {
  message?: string | null;
  userId: string;
  email?: string | null;
  mfaEnabled: boolean;
  validated: boolean;
  authenticatorConfigured?: boolean | null;
  preAuthToken?: string | null;
  challengeId?: string | null;
};

export type MaintenanceMfaInitiateResponse = {
  statusCode?: number;
  message?: string;
  sent?: boolean;
  channel?: string | null;
  challengeId?: string | null;
  userId?: string | null;
  email?: string | null;
};

export type MaintenanceVerifyMfaResponse = {
  message?: string | null;
  statusCode: number;
  user?: {
    roles?: string[] | null;
    userId?: string | null;
    accountType?: string | null;
  } | null;
};

export type OrganizationContextItem = {
  orgId: string;
  orgName: string;
  portalSlug?: string | null;
  orgRole?: string | null;
  orgType?: string | null;
  portalMode?: string | null;
  subscriptionActive?: boolean | null;
  permissions?: string[] | null;
  authorizationPolicyVersion?: string | null;
  accessScopeType?: string | null;
};

export type OrganizationContextResponse = {
  hasMembership?: boolean;
  defaultOrgId?: string | null;
  organizations?: OrganizationContextItem[] | null;
};

@Injectable({ providedIn: 'root' })
export class MaintenancePortalService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiGatewayUrl;
  private readonly propertyBase = `${environment.apiGatewayUrl}/property-service`;
  private readonly recentMaintenanceLoginKey = 'apexMaintenanceRecentLoginAt';

  verifySession(): Observable<MaintenanceSession> {
    return this.http.get<MaintenanceSession>(`${this.base}/api/v1/user/auth-verify`, {
      withCredentials: true,
    });
  }

  preAuthenticate(payload: {
    email: string;
    password: string;
    rememberMe?: boolean;
  }): Observable<MaintenancePreAuthResponse> {
    return this.http.post<MaintenancePreAuthResponse>(`${this.base}/api/v1/user/pre-authenticate`, payload, {
      withCredentials: true,
    });
  }

  login(payload: {
    email: string;
    password: string;
    rememberMe?: boolean;
  }): Observable<unknown> {
    const body = new URLSearchParams();
    body.set('username', payload.email);
    body.set('password', payload.password);
    body.set('grant_type', 'password');
    body.set('rememberMe', String(payload.rememberMe === true));

    return this.http.post(`${this.base}/api/v1/user/login`, body.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      withCredentials: true,
    });
  }

  getMfaDeliveryCapabilities(payload: { userId: string; preAuthToken: string; purpose: 'LOGIN' }) {
    return this.http.post<MfaDeliveryCapabilities>(
      `${this.base}/api/v1/user/mfa/delivery-capabilities`, payload, {
        withCredentials: true,
      },
    ).pipe(
      timeout(8000),
      map((response) => ({
        smsAvailable: response?.smsAvailable === true,
        smsDestination: response?.smsAvailable === true && typeof response.smsDestination === 'string'
          ? response.smsDestination : null,
      })),
      catchError(() => of({ smsAvailable: false, smsDestination: null })),
    );
  }

  initiateEmailMfa(payload: {
    challengeId?: string | null;
    userId?: string | null;
  }): Observable<MaintenanceMfaInitiateResponse> {
    return this.initiateMfa({ ...payload, channel: 'EMAIL' });
  }

  initiateMfa(payload: { preAuthToken?: string | null;
    channel: MaintenanceMfaChannel;
    challengeId?: string | null;
    userId?: string | null;
  }): Observable<MaintenanceMfaInitiateResponse> {
    const body: Record<string, unknown> = {
      purpose: 'LOGIN',
      channel: payload.channel,
    };

    if (payload.challengeId) {
      body['challengeId'] = payload.challengeId;
    }
    if (payload.userId && (!payload.challengeId || payload.channel === 'SMS')) {
      body['userId'] = payload.userId;
    }

    if (payload.preAuthToken) {
      body['preAuthToken'] = payload.preAuthToken;
    }

    return this.http.post<MaintenanceMfaInitiateResponse>(`${this.base}/api/v1/user/send-token`, body, {
      withCredentials: true,
    }).pipe(
      map((response) => {
        const confirmed = response?.statusCode === 200
          || (response?.statusCode === undefined && response?.sent === true);
        if (!confirmed || response?.sent === false) {
          throw new Error(response?.message || 'We could not send your security code.');
        }
        return { ...response, sent: true, channel: response.channel ?? payload.channel };
      }),
    );
  }

  verifyEmailMfa(payload: {
    otpCode: string;
    challengeId?: string | null;
    userId?: string | null;
    rememberMe?: boolean;
    preAuthToken?: string | null;
  }): Observable<MaintenanceVerifyMfaResponse> {
    return this.verifyMfa({ ...payload, channel: 'EMAIL' });
  }

  verifyMfa(payload: {
    otpCode: string;
    channel: MaintenanceMfaChannel;
    challengeId?: string | null;
    userId?: string | null;
    rememberMe?: boolean;
    preAuthToken?: string | null;
  }): Observable<MaintenanceVerifyMfaResponse> {
    const body: Record<string, unknown> = {
      otpCode: payload.otpCode,
      purpose: 'LOGIN',
      channel: payload.channel,
      rememberMe: payload.rememberMe === true,
    };

    if (payload.challengeId) {
      body['challengeId'] = payload.challengeId;
    }
    if (payload.userId && (!payload.challengeId || payload.channel === 'SMS')) {
      body['userId'] = payload.userId;
    }
    // Proves this code belongs to the password step that started the login.
    if (payload.preAuthToken) {
      body['preAuthToken'] = payload.preAuthToken;
    }

    return this.http.post<MaintenanceVerifyMfaResponse>(`${this.base}/api/v1/user/verify`, body, {
      withCredentials: true,
    });
  }

  markMaintenanceLoginVerified(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.sessionStorage.setItem(this.recentMaintenanceLoginKey, String(Date.now()));
    } catch {
      // Storage is best-effort only.
    }
  }

  hasRecentMaintenanceLoginGrace(maxAgeMs = 10000): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      const timestamp = Number(window.sessionStorage.getItem(this.recentMaintenanceLoginKey) || '0');
      return Number.isFinite(timestamp) && timestamp > 0 && Date.now() - timestamp <= maxAgeMs;
    } catch {
      return false;
    }
  }

  getOrganizationContext(): Observable<OrganizationContextResponse> {
    return this.http.get<OrganizationContextResponse>(`${this.propertyBase}/organization/context`, {
      withCredentials: true,
    });
  }

  assignees(orgId: string): Observable<Array<{ userId: string; displayName: string }>> {
    return this.http.get<Array<{ userId: string; displayName: string }>>(`${this.base}/maintenance-service/organizations/${encodeURIComponent(orgId)}/maintenance/assignees`, { withCredentials: true });
  }
  assign(orgId: string, id: string, user: { userId: string; displayName: string }): Observable<WorkOrder> {
    return this.http.post<WorkOrder>(`${this.base}/maintenance-service/organizations/${encodeURIComponent(orgId)}/maintenance/requests/${encodeURIComponent(id)}/assign`,
      { assignedToUserId: user.userId, assignedToName: user.displayName }, { withCredentials: true });
  }
  attachments(orgId: string, id: string): Observable<WorkAttachment[]> {
    return this.http.get<WorkAttachment[]>(`${this.base}/maintenance-service/organizations/${encodeURIComponent(orgId)}/maintenance/requests/${encodeURIComponent(id)}/attachments`, { withCredentials: true });
  }
  upload(orgId: string, id: string, files: File[]): Observable<WorkAttachment[]> {
    const body = new FormData(); files.forEach(file => body.append('files', file));
    return this.http.post<WorkAttachment[]>(`${this.base}/maintenance-service/organizations/${encodeURIComponent(orgId)}/maintenance/requests/${encodeURIComponent(id)}/attachments`, body, { withCredentials: true });
  }

  listWork(orgId: string, page = 0, status = ''): Observable<WorkPage> {
    let params = new HttpParams().set('page', page).set('size', 20);
    if (status) params = params.set('status', status);
    return this.http.get<WorkPage>(`${this.base}/maintenance-service/organizations/${encodeURIComponent(orgId)}/maintenance/requests`, { params, withCredentials: true });
  }

  summary(orgId: string): Observable<WorkSummary> {
    return this.http.get<WorkSummary>(`${this.base}/maintenance-service/organizations/${encodeURIComponent(orgId)}/maintenance/summary`, { withCredentials: true });
  }

  /** scheduledForLocal is the visit's wall-clock time (yyyy-MM-ddTHH:mm, no offset), read in the workspace's time zone. */
  changeStatus(orgId: string, id: string, status: string, reason: string, scheduledForLocal?: string): Observable<WorkOrder> {
    return this.http.post<WorkOrder>(`${this.base}/maintenance-service/organizations/${encodeURIComponent(orgId)}/maintenance/requests/${encodeURIComponent(id)}/status`,
      { status, reason, scheduledForLocal }, { withCredentials: true });
  }

  logout(): Observable<string> {
    return this.http.post(`${this.base}/api/v1/user/logout`, {}, {
      responseType: 'text',
      withCredentials: true,
    });
  }
}

export type WorkOrder = { id: string; organizationId: string; title: string; description: string; status: string;
  priority: string; propertyName?: string; unitLabel?: string; assignedToName?: string; scheduledFor?: string; dueAt?: string };
export type WorkPage = { content: WorkOrder[]; totalPages: number; totalElements: number; number: number };
export type WorkSummary = { total: number; active: number; overdue: number; scheduled: number; resolved: number; closed: number };

export type WorkAttachment = { id: string; fileName: string; url: string };
